import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MachineFilter } from '@shared/entities/machineFilter';
import { ApiService } from '@shared/services';
import { environment } from '@environments/environment';
import { BehaviorSubject, map, Observable, Subject, shareReplay, switchMap, take } from 'rxjs';

const LOG_PREFIX = '[Api][MachineFilters] ';

export interface MachineFilterURL {
  state?: 'OK' | 'KO' | 'OFFLINE';
  serialNumber?: string;
  accountName?: string;
  typology?: string;
  location?: string;
  orderBy?: string;
  locationId?: string;
  direction?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root'
})
export class MachineFiltersService extends ApiService<MachineFilter> {
  private http = inject(HttpClient);
  private refresh$: Subject<boolean>;
  all$: Observable<MachineFilter>;
  currentFilters: MachineFilterURL = {};

  set filters(value: MachineFilterURL) {
    this.currentFilters = value;
    this.refresh(true);
  }

  constructor(private activatedRoute: ActivatedRoute) {
    const masterUrl = '/machines/filters';
    const detailUrl = '/machines/filters';

    super(MachineFilter, masterUrl, detailUrl);

    this.refresh$ = new BehaviorSubject<boolean>(true);
    this.all$ = this.refresh$.pipe(
      switchMap(() => {
        const { locationId } = this.activatedRoute.snapshot.queryParams;
        if (locationId || this.currentFilters.locationId) {
          this.setUrlParameter('locationId', locationId || this.currentFilters.locationId);
        }
        if (this.currentFilters.accountName) {
          this.setUrlParameter('accountName', this.currentFilters.accountName);
        } else {
          this.removeUrlParameter('accountName');
        }
        return this.findOne('');
      }),
      shareReplay(1)
    );
  }

  refresh(value: boolean) {
    this.logger.debug(LOG_PREFIX + 'refresh');
    this.refresh$.next(value);

    return this.all$.pipe(take(1));
  }

  /**
   * One-off lookup of the serial numbers that have the given module enabled, independent of
   * the shared `all$`/`currentFilters` state above so it can't leak into other pages reusing
   * this (root-provided) service.
   */
  getSerialNumbersForModule(moduleCode: string): Observable<string[]> {
    return this.http
      .get<MachineFilter>(`${environment.apiBaseUrl}/machines/filters`, { params: { moduleCode } })
      .pipe(map((response) => response.serialNumber || []));
  }
}
